import { useEffect, useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { renderTimeViewClock } from "@mui/x-date-pickers/timeViewRenderers";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  Typography,
} from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";

import { InputController } from "@shared/ui/form";
import UploadFile from "@vendor/examples/UploadFile";
import { ErrorAlert, SuccessAlert } from "@shared/ui/alerts";

import { buildPostulateCandidateSchema } from "../schema/postulateCandidateSchema";
import { usePostulateCandidate } from "../hooks/usePostulateCandidate";
import {
  DAYS,
  defaultInterviewDateTime,
  handleShouldDisableDate,
  handleShouldDisableTime,
} from "../utils";
import {
  buildVacancyApplicationFormData,
  extractPostulationErrorMessage,
  toLocalISOString,
  toHourMinute,
  dayNameFromDate,
} from "../lib/vacancyApplication";

import type { TFunction } from "i18next";

export interface PostulateCandidateDialogProps {
  handleClose: () => void;
  open: boolean;
  vacancyId?: string;
  vacancyTitle?: string;
  notLoading: () => void;
  loading: () => void;
  t: TFunction;
}

/** Valores del formulario de postulacion tal como los registra react-hook-form. */
interface PostulateFormValues {
  firstName: string;
  lastName: string;
  email: string;
  interviewDateTime: Date | null;
  interviewDay: string;
  interviewHour: string;
  cover: File | null;
}

export const PostulateCandidateDialog = ({
  handleClose,
  open,
  vacancyId,
  vacancyTitle,
  notLoading,
  loading,
  t,
}: PostulateCandidateDialogProps) => {
  const schema = useMemo(() => buildPostulateCandidateSchema(t), [t]);
  const { mutateAsync: postulate } = usePostulateCandidate();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isValid },
  } = useForm<PostulateFormValues>({
    resolver: yupResolver(schema) as never,
    mode: "onChange",
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      interviewDateTime: defaultInterviewDateTime(),
      interviewDay: DAYS[defaultInterviewDateTime().getDay() - 1],
      interviewHour: toHourMinute(defaultInterviewDateTime()),
      cover: null as never,
    },
    shouldUnregister: false,
  });

  const [file, setFile] = useState<File | null>(null);
  const [selectedDateTime, setSelectedDateTime] = useState(defaultInterviewDateTime());
  const [meridiem, setMeridiem] = useState("AM");

  const resetField = () => {
    const defaultInterviewDateTimeValue = defaultInterviewDateTime();
    setSelectedDateTime(defaultInterviewDateTimeValue);
    reset({
      firstName: "",
      lastName: "",
      email: "",
      interviewDateTime: defaultInterviewDateTimeValue as never,
      cover: null as never,
    });
    setFile(null);
  };

  const handleChangeFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      // El `if` garantiza al menos un archivo.
      const selected = e.target.files![0]!;
      setFile(selected);
      setValue("cover", selected as never, { shouldValidate: true });
    } else {
      setFile(null);
      setValue("cover", null as never, { shouldValidate: true });
    }
  };

  const onClose = () => {
    setFile(null);
    resetField();
    handleClose();
  };

  const onRegister = (value: PostulateFormValues) => {
    handleClose();
    loading();

    postulate(buildVacancyApplicationFormData(value as never, vacancyId as string))
      .then(() => {
        notLoading();
        resetField();
        SuccessAlert(t("candidateNomination"), t("doneSuccessfully", { a: "a" })).then();
      })
      .catch((error) => {
        notLoading();
        resetField();
        ErrorAlert("Error", extractPostulationErrorMessage(error, t)).then();
      });
  };

  const handleOnChangeInterviewDateTime = (newValue: Date | null, field: { onChange: (v: Date) => void }) => {
    if (!newValue) return;
    const updatedDate = new Date(newValue);
    if (isNaN(updatedDate.getTime())) return;
    if (updatedDate.getTime() === defaultInterviewDateTime().getTime()) return;

    setSelectedDateTime(updatedDate);
    field.onChange(updatedDate);

    const fieldOptions = { shouldValidate: true, shouldDirty: true, shouldTouch: true };
    setValue("interviewDateTime", toLocalISOString(updatedDate) as never, fieldOptions);
    setValue("interviewDay", dayNameFromDate(updatedDate), fieldOptions);
    setValue("interviewHour", toHourMinute(updatedDate), fieldOptions);
    setMeridiem(updatedDate.getHours() >= 12 ? "PM" : "AM");
  };

  const allFieldsValid = () => {
    const values = watch();
    const requiredFields = ["firstName", "lastName", "email", "interviewDateTime", "cover"];
    return requiredFields.every((field) => !!values[field as keyof PostulateFormValues]) && !Object.keys(errors).length;
  };

  useEffect(() => {
    const isPM = selectedDateTime.getHours() >= 12;
    setMeridiem(isPM ? "PM" : "AM");
  }, [selectedDateTime]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit(onRegister)}>
        <DialogTitle>{`${t("nominateCandidates")} ${vacancyTitle}`}</DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid size={12}>
              <FormControl fullWidth>
                <InputController
                  name="firstName"
                  control={control}
                  type="text"
                  defaultValue=""
                  label={t("name")}
                />
                {errors.firstName && (
                  <span style={{ fontSize: 12, color: "red" }}>{errors.firstName.message}</span>
                )}
              </FormControl>
            </Grid>
            <Grid size={12}>
              <FormControl fullWidth>
                <InputController
                  name="lastName"
                  control={control}
                  type="text"
                  defaultValue=""
                  label={t("lastname")}
                />
                {errors.lastName && (
                  <span style={{ fontSize: 12, color: "red" }}>{errors.lastName.message}</span>
                )}
              </FormControl>
            </Grid>
            <Grid size={12}>
              <FormControl fullWidth>
                <InputController
                  name="email"
                  control={control}
                  type="email"
                  defaultValue=""
                  label={t("mailCandidate")}
                />
                {errors.email && (
                  <span style={{ fontSize: 12, color: "red" }}>{errors.email.message}</span>
                )}
              </FormControl>
            </Grid>
            <Grid size={12}>
              <FormControl fullWidth>
                <Controller
                  name="interviewDateTime"
                  control={control}
                  render={({ field }) => (
                    <>
                      <div key={`${selectedDateTime.getTime()}-${meridiem}`}>
                        <DateTimePicker
                          label={t("availabilityForInterview")}
                          value={selectedDateTime}
                          onChange={(newValue) => {
                            handleOnChangeInterviewDateTime(newValue, field);
                          }}
                          minDate={defaultInterviewDateTime()}
                          shouldDisableDate={handleShouldDisableDate}
                          shouldDisableTime={handleShouldDisableTime}
                          ampm={true}
                          format="dd/MM/yyyy hh:mm a"
                          minutesStep={5}
                          disablePast
                          viewRenderers={{
                            hours: renderTimeViewClock,
                            minutes: renderTimeViewClock,
                            seconds: renderTimeViewClock,
                          }}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: !!(
                                errors.interviewDateTime ||
                                errors.interviewHour ||
                                errors.interviewDay
                              ),
                              helperText:
                                errors.interviewDateTime?.message ||
                                errors.interviewHour?.message ||
                                errors.interviewDay?.message,
                            },
                          }}
                        />
                      </div>
                      <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                        {t("feedbackInterviewDateTime")}
                      </Typography>
                    </>
                  )}
                />
              </FormControl>
            </Grid>
            <Grid size={12}>
              <Box sx={{ mb: 2 }}>
                <UploadFile
                  id="coverPage"
                  nameInput="cover"
                  file={file}
                  textButton={t("uploadCV")}
                  fileLoaded={t("uploadedResume")}
                  onChangeFile={handleChangeFile}
                />
                {errors.cover && (
                  <span style={{ fontSize: 12, color: "red" }}>{errors.cover?.message}</span>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t("cancel")}</Button>
          <Button
            type="submit"
            disabled={!isValid || !allFieldsValid()}
            sx={{
              "&.Mui-disabled": {
                backgroundColor: "rgba(0, 0, 0, 0.12)",
                color: "rgba(0, 0, 0, 0.26)",
              },
            }}
          >
            {t("postulate")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
